import {Alert} from 'src/design/atoms/Alert';
import {Anchor} from 'src/design/atoms/Anchor';
import {List} from 'src/design/atoms/List';
import React, {useCallback, useEffect, useRef} from 'react';
import {AlertIcon, IconSize} from 'src/design/atoms/icons/CommonIcons';
import type {IFormFieldV2} from 'src/types/FormV2';

export interface ValidationErrors {
  [key: string]: string;
}

export interface FormValidationSummaryProps {
  errors: ValidationErrors;
  formMeta: IFormFieldV2[];
}

export const FormValidationSummary = React.memo<FormValidationSummaryProps>(
  ({errors, formMeta}) => {
    const alertRef = useRef<HTMLDivElement>(null);
    const prevErrorCountRef = useRef(0);

    const handleFieldClick = useCallback(
      (fieldName: string) => (e: React.MouseEvent) => {
        e.preventDefault();
        const fieldEl = document.getElementById(`form-field-${fieldName}`);
        if (fieldEl) {
          fieldEl.scrollIntoView({behavior: 'smooth', block: 'center'});
          const firstInput = fieldEl.querySelector<HTMLElement>(
            'input, textarea, select, button[role="combobox"]'
          );
          firstInput?.focus();
        }
      },
      []
    );

    // Order errors by field position in formMeta
    const orderedErrors = formMeta
      .filter(field => errors[field.name] !== undefined)
      .map(field => ({
        name: field.name,
        label: field.label,
        message: errors[field.name],
      }));

    const errorCount = orderedErrors.length;

    useEffect(() => {
      if (
        errorCount > 0 &&
        prevErrorCountRef.current === 0 &&
        alertRef.current
      ) {
        alertRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});
      }
      prevErrorCountRef.current = errorCount;
    }, [errorCount]);

    if (orderedErrors.length === 0) {
      return null;
    }

    return (
      <Alert
        ref={alertRef}
        color="red"
        mt="md"
        mb="md"
        maw={{base: '100%', xl: '1200px'}}
        icon={<AlertIcon size={IconSize.md} />}
        title={`Please fix ${orderedErrors.length} error${
          orderedErrors.length !== 1 ? 's' : ''
        } before saving`}>
        <List size="sm" aria-label="Validation errors">
          {orderedErrors.map(({name, label, message}) => (
            <List.Item key={name}>
              <Anchor
                size="sm"
                c="blue"
                href={`#form-field-${name}`}
                onClick={handleFieldClick(name)}>
                {label}
              </Anchor>{' '}
              — {message}
            </List.Item>
          ))}
        </List>
      </Alert>
    );
  }
);
